/**
 * LanguageContext
 *
 * Detecção de idioma (prioridade):
 * 0. Path da URL           →  /es* →  es-UY  (permite links compartilháveis e SEO)
 * 1. localStorage (preferência salva pelo usuário)
 * 2. Geolocalização por IP  →  UY  →  es-UY
 * 3. Idioma do navegador    →  es* →  es-UY
 * 4. Fallback               →  pt-BR
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import ptBR, { type LandingTranslations } from '@/locales/pt-BR';
import esUY from '@/locales/es-UY';

export type Locale = 'pt-BR' | 'es-UY';

const STORAGE_KEY = 'condoclick_locale';
const GEO_CACHE_KEY = 'condoclick_geo';
const GEO_CACHE_TTL = 30 * 24 * 60 * 60 * 1000; // 30 dias em ms

/* ── Lê cache de geolocalização (retorna null se expirado/ausente) ── */
function readGeoCache(): string | null {
  try {
    const raw = localStorage.getItem(GEO_CACHE_KEY);
    if (!raw) return null;
    const { country, ts } = JSON.parse(raw) as { country: string; ts: number };
    if (Date.now() - ts > GEO_CACHE_TTL) {
      localStorage.removeItem(GEO_CACHE_KEY);
      return null;
    }
    return country;
  } catch {
    return null;
  }
}

/* ── Salva cache de geolocalização ── */
function writeGeoCache(country: string) {
  try {
    localStorage.setItem(GEO_CACHE_KEY, JSON.stringify({ country, ts: Date.now() }));
  } catch { /* quota exceeded — ignora */ }
}

const locales: Record<Locale, LandingTranslations> = {
  'pt-BR': ptBR,
  'es-UY': esUY,
};

/* ── Infere locale a partir do país detectado por IP ── */
function localeFromCountry(countryCode: string): Locale {
  if (countryCode === 'UY') return 'es-UY';
  if (countryCode === 'BR') return 'pt-BR';
  // Outros países hispânicos → espanhol
  const hispanicCountries = [
    'AR','CL','CO','PE','MX','EC','BO','PY','VE','CR','GT','HN','PA','SV','NI','DO','CU','PR',
  ];
  if (hispanicCountries.includes(countryCode)) return 'es-UY';
  return 'pt-BR';
}

/* ── Infere locale a partir do idioma do navegador ── */
function localeFromBrowser(): Locale {
  const lang = (navigator.language || '').toLowerCase();
  if (lang.startsWith('es')) return 'es-UY';
  return 'pt-BR';
}

/* ── Mapa de rotas PT ↔ ES ─────────────────────────────────────────── */
const PT_TO_ES_MAP: Record<string, string> = {
  '/':                 '/es',
  '/franqueado':       '/es/franqueado',
  '/administradoras':  '/es/administradoras',
  '/sindicos':         '/es/sindicos',
  '/suporte':          '/es/soporte',
  '/contato':          '/es/contacto',
  '/sobre':            '/es/sobre',
  '/privacidade':        '/es/privacidad',
  '/termos':             '/es/terminos',
  '/artigos':            '/es/articulos',
  '/checkout/sucesso':   '/es/checkout/exito',
  '/arquitetura-lgpd':   '/es/arquitectura-lgpd',
};

const ES_TO_PT_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(PT_TO_ES_MAP).map(([pt, es]) => [es, pt])
);

/** Detecta locale a partir do path (retorna null se indeterminado) */
function localeFromPath(pathname: string): Locale | null {
  if (pathname === '/es' || pathname.startsWith('/es/')) return 'es-UY';
  return null;
}

/**
 * Retorna o path equivalente no locale alvo.
 * Usado pelo LanguageSwitcher e demais componentes que alternam idioma.
 */
export function getLocalePath(pathname: string, targetLocale: Locale): string {
  const clean = pathname.split('?')[0];
  if (targetLocale === 'es-UY') {
    if (clean.startsWith('/es')) return clean;
    if (clean.startsWith('/artigos/')) return clean.replace('/artigos/', '/es/articulos/');
    return PT_TO_ES_MAP[clean] ?? '/es';
  } else {
    if (!clean.startsWith('/es')) return clean;
    if (clean.startsWith('/es/articulos/')) return clean.replace('/es/articulos/', '/artigos/');
    return ES_TO_PT_MAP[clean] ?? '/';
  }
}

/* ── Contexto ── */
interface LanguageContextValue {
  locale: Locale;
  t: LandingTranslations;
  setLocale: (locale: Locale) => void;
  /** true enquanto aguarda resposta da geolocalização por IP */
  detecting: boolean;
}

const LanguageContext = createContext<LanguageContextValue>({
  locale: 'pt-BR',
  t: ptBR,
  setLocale: () => {},
  detecting: false,
});

export const useLanguage = () => useContext(LanguageContext);

/* ── Provider ── */
export function LanguageProvider({ children }: { children: ReactNode }) {
  // Prioridade 0: URL path; 1: localStorage; 2: browser
  const [locale, setLocaleState] = useState<Locale>(() => {
    const pathLocale = localeFromPath(window.location.pathname);
    if (pathLocale) return pathLocale;
    const saved = localStorage.getItem(STORAGE_KEY) as Locale | null;
    if (saved && locales[saved]) return saved;
    return localeFromBrowser();
  });
  const [detecting, setDetecting] = useState(false);

  /* Detecta país via IP apenas se o locale NÃO veio da URL nem de localStorage */
  useEffect(() => {
    if (localeFromPath(window.location.pathname)) return; // URL tem prioridade
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return; // Respeita preferência do usuário

    // Verifica cache de geo antes de chamar a API
    const cached = readGeoCache();
    if (cached) {
      setLocaleState(localeFromCountry(cached));
      return;
    }

    setDetecting(true);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 4000);

    fetch('https://ipapi.co/json/', { signal: controller.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data: { country_code?: string }) => {
        if (data?.country_code) {
          writeGeoCache(data.country_code);
          const detected = localeFromCountry(data.country_code);
          setLocaleState(detected);
        }
      })
      .catch(() => {
        /* falha silenciosa (429, timeout, etc.) — mantém default do navegador */
      })
      .finally(() => {
        clearTimeout(timeout);
        setDetecting(false);
      });

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, []);

  const setLocale = useCallback((newLocale: Locale) => {
    localStorage.setItem(STORAGE_KEY, newLocale);
    setLocaleState(newLocale);
  }, []);

  const value: LanguageContextValue = {
    locale,
    t: locales[locale],
    setLocale,
    detecting,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}
